/* eslint-disable @backstage/no-deprecated-bui-tokens -- BUI 0.16 still
 * consumes the solid token family internally for primary and selection
 * controls. Keep the compatibility bridge centralized in this theme. */
type CssValue = string | number;
type VerdantCssVariable = `--bui-${string}` | `--verdant-${string}`;
type CssVariableMap = Record<VerdantCssVariable, CssValue>;

export type VerdantSemanticColors = {
  app: string;
  surface: string;
  raised: string;
  text: string;
  muted: string;
  disabled: string;
  border: string;
  borderStrong: string;
  primary: string;
  primaryHover: string;
  primaryPressed: string;
  primaryDisabled: string;
  primarySoft: string;
  primaryContrast: string;
  primaryContrastDisabled: string;
  focus: string;
  info: string;
  infoSoft: string;
  warning: string;
  warningSoft: string;
  critical: string;
  criticalSoft: string;
};

const foundation = {
  breakpoints: {
    mobileMax: 767,
  },
  shell: {
    mobileTopBarHeight: '4rem',
    desktopSidebarWidth: '14rem',
  },
} as const;

const navigation = {
  background: '#0D1512',
  indicator: '#43D69B',
  color: '#A9B8B0',
  selectedColor: '#F5FBF8',
  hoverBackground: '#17241F',
  submenuBackground: '#111D18',
  divider: '#203029',
  descriptor: '#A9B8B0',
  itemFontSize: '0.875rem',
  descriptorFontSize: '0.75rem',
} as const;

const lightSemantic = {
  app: '#F7F9F8',
  surface: '#FFFFFF',
  raised: '#F1F5F3',
  text: '#17211D',
  muted: '#647169',
  disabled: '#98A39D',
  border: '#DDE5E1',
  borderStrong: '#AAB8B1',
  primary: '#087A4F',
  primaryHover: '#06633F',
  primaryPressed: '#054C31',
  primaryDisabled: '#9ECBBB',
  primarySoft: '#DDF5E9',
  primaryContrast: '#FFFFFF',
  primaryContrastDisabled: '#EFF7F3',
  focus: '#087A4F',
  info: '#1C64C8',
  infoSoft: '#E6F0FF',
  warning: '#9A5A00',
  warningSoft: '#FFF1D6',
  critical: '#972D38',
  criticalSoft: '#FDE8EA',
} as const satisfies VerdantSemanticColors;

const darkSemantic = {
  app: '#0A0F0D',
  surface: '#101714',
  raised: '#16201C',
  text: '#F2F6F4',
  muted: '#9DAAA3',
  disabled: '#66736D',
  border: '#26332D',
  borderStrong: '#48584F',
  primary: '#43D69B',
  primaryHover: '#63E3AF',
  primaryPressed: '#2FB982',
  primaryDisabled: '#285C47',
  primarySoft: '#123629',
  primaryContrast: '#082016',
  primaryContrastDisabled: '#7EAE9A',
  focus: '#56E3AB',
  info: '#78A8FF',
  infoSoft: '#142947',
  warning: '#F2B84B',
  warningSoft: '#3A2A0D',
  critical: '#FF7B7B',
  criticalSoft: '#3A171A',
} as const satisfies VerdantSemanticColors;

const sharedCssVariables = {
  '--bui-black': '#0A0F0D',
  '--bui-white': '#FFFFFF',
  '--verdant-navigation-background': navigation.background,
  '--verdant-navigation-color': navigation.color,
  '--verdant-navigation-selected': navigation.selectedColor,
  '--verdant-navigation-hover': navigation.hoverBackground,
  '--verdant-navigation-divider': navigation.divider,
  '--verdant-navigation-descriptor': navigation.descriptor,
  '--verdant-navigation-item-size': navigation.itemFontSize,
  '--verdant-navigation-descriptor-size': navigation.descriptorFontSize,
  '--verdant-mobile-topbar-height': foundation.shell.mobileTopBarHeight,
  '--verdant-desktop-sidebar-width': foundation.shell.desktopSidebarWidth,
  '--verdant-mark-background': '#173C2E',
  '--verdant-mark-accent': '#58E0A9',
  '--verdant-mark-node': '#E8FFF5',
} as const satisfies CssVariableMap;

const lightCssVariables = {
  '--bui-ring': lightSemantic.focus,
  '--bui-scrollbar': '#D7E0DC',
  '--bui-scrollbar-thumb': '#98A59F',
  '--bui-gray-1': lightSemantic.app,
  '--bui-gray-2': lightSemantic.raised,
  '--bui-gray-3': lightSemantic.border,
  '--bui-gray-4': '#C8D3CE',
  '--bui-gray-5': lightSemantic.borderStrong,
  '--bui-gray-6': '#7D8B84',
  '--bui-gray-7': lightSemantic.muted,
  '--bui-gray-8': '#45534C',
  '--bui-gray-9': '#2C3832',
  '--bui-gray-10': lightSemantic.text,
  '--bui-gray-11': '#0A0F0D',
  '--bui-bg-app': lightSemantic.app,
  '--bui-bg-neutral-1': lightSemantic.surface,
  '--bui-bg-neutral-2': lightSemantic.raised,
  '--bui-bg-neutral-3': lightSemantic.surface,
  '--bui-bg-neutral-4': '#EDF2EF',
  '--bui-bg-solid': lightSemantic.primary,
  '--bui-bg-solid-hover': lightSemantic.primaryHover,
  '--bui-bg-solid-pressed': lightSemantic.primaryPressed,
  '--bui-bg-solid-disabled': lightSemantic.primaryDisabled,
  '--bui-fg-primary': lightSemantic.text,
  '--bui-fg-secondary': lightSemantic.muted,
  '--bui-fg-disabled': lightSemantic.disabled,
  '--bui-fg-solid': lightSemantic.primaryContrast,
  '--bui-fg-solid-disabled': lightSemantic.primaryContrastDisabled,
  '--bui-fg-positive': lightSemantic.primary,
  '--bui-fg-negative': lightSemantic.critical,
  '--bui-fg-warning': lightSemantic.warning,
  '--bui-fg-announcement': lightSemantic.info,
  '--bui-border-1': lightSemantic.border,
  '--bui-border-2': lightSemantic.borderStrong,
  '--bui-accent-bg': lightSemantic.primary,
  '--bui-accent-bg-hover': lightSemantic.primaryHover,
  '--bui-accent-bg-disabled': lightSemantic.primaryDisabled,
  '--bui-accent-fg': lightSemantic.primaryContrast,
  '--bui-accent-fg-disabled': lightSemantic.primaryContrastDisabled,
  '--bui-announcement-bg': lightSemantic.info,
  '--bui-announcement-bg-hover': '#174F9E',
  '--bui-announcement-bg-disabled': '#9CB9DF',
  '--bui-announcement-bg-subdued': lightSemantic.infoSoft,
  '--bui-announcement-bg-subdued-hover': '#D6E6FC',
  '--bui-announcement-bg-subdued-disabled': '#EDF3FB',
  '--bui-announcement-border': '#8AB0E5',
  '--bui-announcement-fg': '#FFFFFF',
  '--bui-announcement-fg-disabled': '#EDF4FF',
  '--bui-announcement-fg-subdued': '#174F9E',
  '--bui-announcement-fg-subdued-disabled': '#718BAD',
  '--bui-warning-bg': lightSemantic.warning,
  '--bui-warning-bg-hover': '#7B4800',
  '--bui-warning-bg-disabled': '#D9B983',
  '--bui-warning-bg-subdued': lightSemantic.warningSoft,
  '--bui-warning-bg-subdued-hover': '#F9E4BB',
  '--bui-warning-bg-subdued-disabled': '#FBF1DF',
  '--bui-warning-border': '#D9A44F',
  '--bui-warning-fg': '#FFFFFF',
  '--bui-warning-fg-disabled': '#FFF8EC',
  '--bui-warning-fg-subdued': '#754500',
  '--bui-warning-fg-subdued-disabled': '#9C8056',
  '--bui-negative-bg': lightSemantic.critical,
  '--bui-negative-bg-hover': '#7E2630',
  '--bui-negative-bg-disabled': '#DFA2A8',
  '--bui-negative-bg-subdued': lightSemantic.criticalSoft,
  '--bui-negative-bg-subdued-hover': '#F8D7DA',
  '--bui-negative-bg-subdued-disabled': '#FAEEF0',
  '--bui-negative-border': '#E3A1A7',
  '--bui-negative-fg': '#FFFFFF',
  '--bui-negative-fg-disabled': '#FFF3F4',
  '--bui-negative-fg-subdued': lightSemantic.critical,
  '--bui-negative-fg-subdued-disabled': '#A97C80',
  '--bui-positive-bg': lightSemantic.primary,
  '--bui-positive-bg-hover': lightSemantic.primaryHover,
  '--bui-positive-bg-disabled': lightSemantic.primaryDisabled,
  '--bui-positive-bg-subdued': lightSemantic.primarySoft,
  '--bui-positive-bg-subdued-hover': '#C9EBDA',
  '--bui-positive-bg-subdued-disabled': '#EAF4EF',
  '--bui-positive-border': '#85C8AB',
  '--bui-positive-fg': '#FFFFFF',
  '--bui-positive-fg-disabled': '#F0F8F4',
  '--bui-positive-fg-subdued': lightSemantic.primaryHover,
  '--bui-positive-fg-subdued-disabled': '#6E9182',
} as const satisfies CssVariableMap;

const darkCssVariables = {
  '--bui-ring': darkSemantic.focus,
  '--bui-scrollbar': '#1B2722',
  '--bui-scrollbar-thumb': '#526159',
  '--bui-bg-app': darkSemantic.app,
  '--bui-bg-neutral-1': darkSemantic.surface,
  '--bui-bg-neutral-2': darkSemantic.raised,
  '--bui-bg-neutral-3': '#19251F',
  '--bui-bg-neutral-4': '#1D2B24',
  '--bui-bg-solid': darkSemantic.primary,
  '--bui-bg-solid-hover': darkSemantic.primaryHover,
  '--bui-bg-solid-pressed': darkSemantic.primaryPressed,
  '--bui-bg-solid-disabled': darkSemantic.primaryDisabled,
  '--bui-fg-primary': darkSemantic.text,
  '--bui-fg-secondary': darkSemantic.muted,
  '--bui-fg-disabled': darkSemantic.disabled,
  '--bui-fg-solid': darkSemantic.primaryContrast,
  '--bui-fg-solid-disabled': darkSemantic.primaryContrastDisabled,
  '--bui-fg-positive': darkSemantic.primary,
  '--bui-fg-negative': darkSemantic.critical,
  '--bui-fg-warning': darkSemantic.warning,
  '--bui-fg-announcement': darkSemantic.info,
  '--bui-border-1': darkSemantic.border,
  '--bui-border-2': darkSemantic.borderStrong,
  '--bui-accent-bg': darkSemantic.primary,
  '--bui-accent-bg-hover': darkSemantic.primaryHover,
  '--bui-accent-bg-disabled': darkSemantic.primaryDisabled,
  '--bui-accent-fg': darkSemantic.primaryContrast,
  '--bui-accent-fg-disabled': darkSemantic.primaryContrastDisabled,
  '--bui-announcement-bg': darkSemantic.info,
  '--bui-announcement-bg-hover': '#91B8FF',
  '--bui-announcement-bg-disabled': '#314C75',
  '--bui-announcement-bg-subdued': darkSemantic.infoSoft,
  '--bui-announcement-bg-subdued-hover': '#1B365B',
  '--bui-announcement-bg-subdued-disabled': '#172237',
  '--bui-announcement-border': '#345B92',
  '--bui-announcement-fg': '#081321',
  '--bui-announcement-fg-disabled': '#89A0C1',
  '--bui-announcement-fg-subdued': '#A9C7FF',
  '--bui-announcement-fg-subdued-disabled': '#5F769E',
  '--bui-warning-bg': darkSemantic.warning,
  '--bui-warning-bg-hover': '#FFC968',
  '--bui-warning-bg-disabled': '#705A2D',
  '--bui-warning-bg-subdued': darkSemantic.warningSoft,
  '--bui-warning-bg-subdued-hover': '#4A3712',
  '--bui-warning-bg-subdued-disabled': '#302613',
  '--bui-warning-border': '#755A25',
  '--bui-warning-fg': '#211500',
  '--bui-warning-fg-disabled': '#A28B5E',
  '--bui-warning-fg-subdued': '#F5C96F',
  '--bui-warning-fg-subdued-disabled': '#977A46',
  '--bui-negative-bg': darkSemantic.critical,
  '--bui-negative-bg-hover': '#FF9999',
  '--bui-negative-bg-disabled': '#713D40',
  '--bui-negative-bg-subdued': darkSemantic.criticalSoft,
  '--bui-negative-bg-subdued-hover': '#502126',
  '--bui-negative-bg-subdued-disabled': '#311A1C',
  '--bui-negative-border': '#75343C',
  '--bui-negative-fg': '#270708',
  '--bui-negative-fg-disabled': '#AF777A',
  '--bui-negative-fg-subdued': '#FFAAAA',
  '--bui-negative-fg-subdued-disabled': '#A66D72',
  '--bui-positive-bg': darkSemantic.primary,
  '--bui-positive-bg-hover': darkSemantic.primaryHover,
  '--bui-positive-bg-disabled': darkSemantic.primaryDisabled,
  '--bui-positive-bg-subdued': darkSemantic.primarySoft,
  '--bui-positive-bg-subdued-hover': '#174634',
  '--bui-positive-bg-subdued-disabled': '#142A21',
  '--bui-positive-border': '#276B50',
  '--bui-positive-fg': darkSemantic.primaryContrast,
  '--bui-positive-fg-disabled': '#7EAE9A',
  '--bui-positive-fg-subdued': '#77E8BA',
  '--bui-positive-fg-subdued-disabled': '#598C77',
} as const satisfies CssVariableMap;

export const verdantTokens = {
  ...foundation,
  navigation,
  modes: {
    light: { semantic: lightSemantic, cssVariables: lightCssVariables },
    dark: { semantic: darkSemantic, cssVariables: darkCssVariables },
  },
} as const;

const formatCssValue = (value: CssValue): CssValue => {
  if (typeof value !== 'string') return value;
  return value
    .replace(/#[\da-f]{6}\b/gi, hex => hex.toLowerCase())
    .replaceAll('"', "'");
};

const renderVariables = (variables: CssVariableMap) =>
  Object.entries(variables)
    .map(([name, value]) => `  ${name}: ${formatCssValue(value)};`)
    .join('\n');

export const renderVerdantCss = () => {
  const mobile = verdantTokens.breakpoints.mobileMax;
  const lightVariables = {
    ...sharedCssVariables,
    ...verdantTokens.modes.light.cssVariables,
  };

  return `/* Generated from verdantTokens.ts. Run the Verdant token test in write mode to update. */
:root,
[data-theme-mode='light'] {
  color-scheme: light;

${renderVariables(lightVariables)}
}

[data-theme-mode='dark'] {
  color-scheme: dark;

${renderVariables(verdantTokens.modes.dark.cssVariables)}
}

html {
  min-height: 100%;
  background: var(--bui-bg-app);
}

body,
#root {
  min-height: 100dvh;
}

body {
  background: var(--bui-bg-app);
  color: var(--bui-fg-primary);
  overflow-wrap: break-word;
}

::selection {
  background: var(--bui-positive-bg-subdued);
  color: var(--bui-fg-primary);
}

:root body div:has(> aside[aria-label='Application sidebar']) {
  min-height: 100dvh;
  padding-left: 0 !important;
  background: var(--bui-bg-app);
  transition: none !important;
}

@media screen and (min-width: ${mobile + 1}px) {
  :root body div:has(> aside[aria-label='Application sidebar']) {
    padding-left: var(--verdant-desktop-sidebar-width) !important;
  }
}

@media (max-width: ${mobile}px) {
  .MuiGrid-container {
    width: 100%;
    max-width: 100%;
    margin-right: 0;
    margin-left: 0;
  }

  .MuiGrid-item,
  .MuiPaper-root {
    min-width: 0;
    max-width: 100%;
  }

  .MuiTableContainer-root {
    max-width: 100%;
    overflow-x: auto;
  }

  .MuiTabs-flexContainer {
    flex-wrap: wrap;
  }

  .MuiTab-root {
    min-width: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
}

@media (forced-colors: active) {
  :focus-visible {
    outline: 2px solid CanvasText;
    outline-offset: 2px;
  }

  a,
  button,
  [role='button'] {
    forced-color-adjust: auto;
  }
}

@media print {
  :root body div:has(> aside[aria-label='Application sidebar']) {
    padding-left: 0 !important;
    transition: none !important;
  }
}
`;
};
